import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import MaterialTable from 'material-table';
import { Paper, Tooltip, IconButton, Typography, Fab } from '@material-ui/core';
import { Refresh, Add } from '@material-ui/icons';
import { hasPermission, BackButton, localization, getDimensionsOfPagination } from '../../sc-utilities';
import Toolbar from '../../sc-utilities/Toolbar';
import { MuiTableRows } from '../../../themes';
import * as actions from './store/unpairedPaletteActions';
import * as action from '../../np-core/Auth/store/authActions';
import { CancelToken } from 'axios';

const styles = theme => ({
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    add: {
        height: '50%'
    },
    customTooltip: {
        marginLeft: -16
    },
    customToolbar: {
        marginLeft: 16
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const UnpairedPaletteList = props => {
    const { classes, tableColumns, saveColumnsFilter, redirectHandler, fetching, onFetchUnpairedPalettes, onFetchUnpairedPalettesCount, translations, activeCustomerID, permissions, tableRef } = props;
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []); const countSource = CancelToken.source();
    const listSource = CancelToken.source();

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: false,
        search: false,
        minBodyHeight: window.innerHeight - 205 < 300 ? 300 : window.innerHeight - 205,
        maxBodyHeight: window.innerHeight - 205,
        pageSizeOptions: rowsPageSizeInit,

        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;

        let count = await onFetchUnpairedPalettesCount(query, { countSource: countSource });
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count.data)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = !count.data ? { data: [] } : await onFetchUnpairedPalettes(query, { listSource: listSource });
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: count.data };
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`UnpairedPalette`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    };

    const tableTitle = () => {
        return <div className={classes.tableTitle}>
            {BackButton(translations, classes.customTooltip)}
            <Typography className={classes.typography}>{translations['unpaired_palettes'] || 'Unpaired palettes'}</Typography>
            {
                hasPermission(permissions, 'unpaired_palette_create_from_external') ?
                    <Fab
                        variant="extended"
                        size="small"
                        color="primary"
                        aria-label="Add"
                        style={{ borderRadius: '1px' }}
                        className={classes.fab}
                        onClick={() => redirectHandler(`/unpairedAssets/unpairedPalette-createFromExternal`)}
                    >
                        <Add size="small" className={classes.add} />
                        {translations["fetch"] || "Fetch"}
                    </Fab> : null
            }
            <Tooltip title={translations["refresh"] || "Refresh"}>
                <IconButton variant={'subtitle1'} aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                    <Refresh id={'refresh_button'} />
                </IconButton>
            </Tooltip>
        </div>
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar
                            data={props}
                            selectedRows={props.selectedRows}
                            translations={translations}
                        />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
        fetching: state.palette.unpairedPalettes.fetching
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchUnpairedPalettesCount: (filter, options) => dispatch(actions.fetchUnpairedPaletteCount(filter, options)),
        onFetchUnpairedPalettes: (filter, options) => dispatch(actions.fetchUnpairedPalette(filter, options)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data))
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(UnpairedPaletteList));